import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  User, 
  Alert, 
  WorkOrder, 
  Equipment, 
  Property,
  Analytics,
  ApiResponse,
  PaginationParams,
  LoginForm
} from '@/types'

// Get API base URL from environment
const getApiBaseUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || '/api'
  
  // Log API URL in development for debugging
  if (import.meta.env.DEV) {
    console.log('🔗 API Base URL:', apiUrl)
    console.log('🌍 Environment:', import.meta.env.MODE)
    console.log('📝 VITE_API_URL env var:', import.meta.env.VITE_API_URL || 'NOT SET (using default /api)')
  }
  
  return apiUrl
}

// Helper function to get Clerk token
// In Clerk v5, we access the Clerk instance via window.Clerk (set by ClerkProvider)
let clerkLoadPromise: Promise<void> | null = null
async function getClerkToken(): Promise<string | null> {
  try {
    // Access Clerk instance from window (set by ClerkProvider)
    const clerk = (window as any).Clerk
    if (!clerk) {
      return null
    }

    // Ensure Clerk has finished loading before reading session/token.
    // This avoids cases where React has mounted but window.Clerk isn't ready yet.
    if (clerk.loaded === false && typeof clerk.load === 'function') {
      if (!clerkLoadPromise) {
        clerkLoadPromise = clerk.load().catch(() => undefined)
      }
      await clerkLoadPromise
    }

    // Get the current session
    const session = clerk.session
    if (!session) {
      return null
    }

    // Get the token from the session
    const jwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE as string | undefined
    const token = jwtTemplate
      ? await session.getToken({ template: jwtTemplate })
      : await session.getToken()
    return token || null
  } catch (error) {
    // Clerk not initialized, user not signed in, or session expired
    console.debug('Could not get Clerk token:', error)
    return null
  }
}

// Custom base query that gets Clerk token
// Note: This requires ClerkProvider to be set up in the app
const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  prepareHeaders: async (headers) => {
    const token = await getClerkToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    // Don't force Content-Type globally (it breaks multipart/form-data uploads).
    headers.set('Accept', 'application/json')
    return headers
  },
})

// Base query with error handling
const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions)
  
  if (result.error) {
    // Only log network/connection errors in development mode
    if (result.error.status === 'FETCH_ERROR' && import.meta.env.DEV) {
      console.debug('API Connection Error:', {
        url: args?.url || 'unknown',
        error: result.error.error
      })
    }
    
    if (result.error.status === 401) {
      // Token missing/expired/invalid. Clear local state and force a fresh auth flow.
      api.dispatch({ type: 'auth/logout' })

      try {
        const clerk = (window as any).Clerk
        if (clerk?.signOut) {
          await clerk.signOut({ redirectUrl: '/login' })
        } else if (typeof window !== 'undefined') {
          window.location.href = '/login'
        }
      } catch {
        if (typeof window !== 'undefined') {
          window.location.href = '/login'
        }
      }
    }
  }
  
  return result
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Alert', 'WorkOrder', 'Equipment', 'Analytics', 'Property'],
  endpoints: (builder) => ({
    // Authentication endpoints - Clerk handles login, this is just for getting user profile
    login: builder.mutation<
      ApiResponse<{ user: User; token: string; permissions: string[] }>,
      LoginForm
    >({
      queryFn: async (): Promise<{ data: ApiResponse<{ user: User; token: string; permissions: string[] }> }> => {
        // Clerk handles authentication, this endpoint just returns the current user profile
        // This should be called after Clerk authentication
        return {
          data: {
            success: false,
            message: 'Use Clerk authentication instead',
            data: undefined as any // TypeScript workaround for missing data field
          }
        }
      },
    }),
    
    logout: builder.mutation<ApiResponse<null>, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    
    refreshToken: builder.mutation<
      ApiResponse<{ token: string }>,
      void
    >({
      query: () => ({
        url: '/auth/refresh',
        method: 'POST',
      }),
    }),
    
    // User endpoints
    getCurrentUser: builder.query<ApiResponse<User>, void>({
      query: () => '/users/me',
      providesTags: ['User'],
    }),
    
    updateUser: builder.mutation<ApiResponse<User>, { id: string; updates: Partial<User> }>({
      query: ({ id, updates }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['User'],
    }),
    
    // Alert endpoints
    getAlerts: builder.query<
      ApiResponse<Alert[]>,
      PaginationParams & {
        priority?: string[]
        status?: string[]
        equipmentType?: string[]
        propertyId?: string
      }
    >({
      query: (params) => ({
        url: '/alerts',
        params,
      }),
      providesTags: ['Alert'],
    }),
    
    getAlert: builder.query<ApiResponse<Alert>, string>({
      query: (id) => `/alerts/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Alert', id }],
    }),
    
    acknowledgeAlert: builder.mutation<ApiResponse<Alert>, string>({
      query: (id) => ({
        url: `/alerts/${id}`,
        method: 'PATCH',
        body: { 
          status: 'acknowledged',
          acknowledgedAt: new Date().toISOString()
        },
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        // Optimistically update the cache for immediate UI feedback
        const patchResult = dispatch(
          api.util.updateQueryData('getAlerts', { limit: 100, sortBy: 'created_at', sortOrder: 'desc' }, (draft) => {
            const alert = draft.data?.find(a => a.id === id)
            if (alert) {
              alert.status = 'acknowledged'
              alert.acknowledgedAt = new Date().toISOString()
            }
          })
        )
        
        try {
          await queryFulfilled
          if (import.meta.env.DEV) {
            console.debug('Alert acknowledged in database:', id)
          }
        } catch (error) {
          // If the API call fails, revert the optimistic update
          patchResult.undo()
          if (import.meta.env.DEV) {
            console.debug('Failed to acknowledge alert in database:', error)
          }
        }
      },
      invalidatesTags: (_result, _error, id) => [{ type: 'Alert', id }, 'Alert'],
    }),
    
    acknowledgeMultipleAlerts: builder.mutation<ApiResponse<Alert[]>, string[]>({
      query: (alertIds) => ({
        url: '/alerts/batch-update',
        method: 'PATCH',
        body: { 
          alertIds,
          updates: {
            status: 'acknowledged',
            acknowledgedAt: new Date().toISOString()
          }
        },
      }),
      invalidatesTags: ['Alert'],
    }),
    
    createWorkOrderFromAlert: builder.mutation<
      ApiResponse<WorkOrder>,
      { alertId: string; workOrderData?: Partial<WorkOrder> }
    >({
      query: ({ alertId, workOrderData }) => ({
        url: `/alerts/${alertId}/create-work-order`,
        method: 'POST',
        body: workOrderData,
      }),
      invalidatesTags: ['Alert', 'WorkOrder'],
    }),
    
    // Work Order endpoints
    getWorkOrders: builder.query<
      ApiResponse<WorkOrder[]>,
      PaginationParams & {
        status?: string[]
        priority?: string[]
        assignedTo?: string[]
        equipmentType?: string[]
        propertyId?: string
      }
    >({
      query: (params) => ({
        url: '/work-orders',
        params,
      }),
      providesTags: ['WorkOrder'],
    }),
    
    getWorkOrder: builder.query<ApiResponse<WorkOrder>, string>({
      query: (id) => `/work-orders/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'WorkOrder', id }],
    }),
    
    createWorkOrder: builder.mutation<ApiResponse<WorkOrder>, Partial<WorkOrder>>({
      query: (workOrderData) => ({
        url: '/work-orders',
        method: 'POST',
        body: workOrderData,
      }),
      invalidatesTags: ['WorkOrder'],
    }),
    
    updateWorkOrder: builder.mutation<
      ApiResponse<WorkOrder>,
      { id: string; updates: Partial<WorkOrder> }
    >({
      query: ({ id, updates }) => ({
        url: `/work-orders/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'WorkOrder', id }, 'WorkOrder'],
    }),
    
    deleteWorkOrder: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `/work-orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WorkOrder'],
    }),
    
    assignWorkOrder: builder.mutation<
      ApiResponse<WorkOrder>,
      { id: string; assignedTo: string }
    >({
      query: ({ id, assignedTo }) => ({
        url: `/work-orders/${id}/assign`,
        method: 'POST',
        body: { assignedTo },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'WorkOrder', id }, 'WorkOrder'],
    }),
    
    updateWorkOrderProgress: builder.mutation<
      ApiResponse<WorkOrder>,
      { 
        id: string
        progress: number
        actualHours?: number
        notes?: string
        attachments?: File[]
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/work-orders/${id}/progress`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'WorkOrder', id }, 'WorkOrder'],
    }),
    
    // Equipment endpoints
    getEquipment: builder.query<
      ApiResponse<Equipment[]>,
      PaginationParams & {
        type?: string[]
        status?: string[]
        healthScore?: { min?: number; max?: number }
        propertyId?: string
        hasAlerts?: boolean
      }
    >({
      query: (params) => ({
        url: '/equipment',
        params,
      }),
      providesTags: ['Equipment'],
    }),
    
    getEquipmentById: builder.query<ApiResponse<Equipment>, string>({
      query: (id) => `/equipment/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Equipment', id }],
    }),
    
    createEquipment: builder.mutation<ApiResponse<Equipment>, Partial<Equipment>>({
      query: (equipmentData) => ({
        url: '/equipment',
        method: 'POST',
        body: equipmentData,
      }),
      invalidatesTags: ['Equipment'],
    }),
    
    updateEquipment: builder.mutation<
      ApiResponse<Equipment>,
      { id: string; updates: Partial<Equipment> }
    >({
      query: ({ id, updates }) => ({
        url: `/equipment/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Equipment', id }, 'Equipment'],
    }),
    
    deleteEquipment: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `/equipment/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Equipment'],
    }),
    
    getEquipmentHistory: builder.query<
      ApiResponse<Equipment['maintenanceHistory']>,
      { equipmentId: string; limit?: number }
    >({
      query: ({ equipmentId, limit = 10 }) => ({
        url: `/equipment/${equipmentId}/history`,
        params: { limit },
      }),
    }),
    
    scheduleMaintenanceForEquipment: builder.mutation<
      ApiResponse<WorkOrder>,
      {
        equipmentId: string
        scheduledDate: string
        type: 'preventive' | 'inspection'
        description?: string
      }
    >({
      query: ({ equipmentId, ...data }) => ({
        url: `/equipment/${equipmentId}/schedule-maintenance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Equipment', 'WorkOrder'],
    }),
    
    // Analytics endpoints
    getDashboardAnalytics: builder.query<
      ApiResponse<Analytics>,
      { timeframe?: '24h' | '7d' | '30d' | '90d' | '1y'; propertyId?: string }
    >({
      query: (params) => ({
        url: '/analytics/dashboard',
        params,
      }),
      providesTags: ['Analytics'],
    }),
    
    getEquipmentAnalytics: builder.query<
      ApiResponse<{
        healthDistribution: Array<{ status: string; count: number }>
        typeDistribution: Array<{ type: string; count: number; avgHealth: number }>
        healthTrends: Array<{ date: string; avgHealth: number }>
        maintenanceCosts: Array<{ month: string; planned: number; emergency: number }>
      }>,
      { timeframe?: string; propertyId?: string }
    >({
      query: (params) => ({
        url: '/analytics/equipment',
        params,
      }),
      providesTags: ['Analytics'],
    }),
    
    getCostAnalytics: builder.query<
      ApiResponse<{
        totalCosts: number
        plannedVsEmergency: { planned: number; emergency: number }
        costsByEquipmentType: Array<{ type: string; cost: number }>
        monthlyTrends: Array<{ month: string; cost: number; savings: number }>
        roi: { totalSavings: number; preventedFailures: number; costAvoidance: number }
      }>,
      { timeframe?: string; propertyId?: string }
    >({
      query: (params) => ({
        url: '/analytics/costs',
        params,
      }),
      providesTags: ['Analytics'],
    }),
    
    // Real-time data endpoints
    getRealtimeData: builder.query<
      ApiResponse<{
        alerts: { new: number; critical: number }
        workOrders: { active: number; overdue: number }
        equipment: { critical: number; avgHealth: number }
        costs: { thisMonth: number; trend: number }
      }>,
      void
    >({
      query: () => '/realtime/dashboard',
      // Note: Polling should be handled at the component level with refetchOnMountOrArgChange
    }),
    
    // File upload endpoints
    uploadFile: builder.mutation<
      ApiResponse<{ url: string; filename: string }>,
      { file: File; type: 'work-order' | 'equipment' | 'profile' }
    >({
      query: ({ file, type }) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', type)
        
        return {
          url: '/upload',
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
    }),

    // Properties endpoints
    getProperties: builder.query<ApiResponse<Property[]>, void>({
      query: () => '/properties',
      providesTags: ['Property'],
    }),

    getProperty: builder.query<ApiResponse<Property>, string>({
      query: (id) => `/properties/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Property', id }],
    }),

    createProperty: builder.mutation<ApiResponse<Property>, Partial<Property>>({
      query: (propertyData) => ({
        url: '/properties',
        method: 'POST',
        body: propertyData,
      }),
      invalidatesTags: ['Property'],
    }),

    updateProperty: builder.mutation<
      ApiResponse<Property>,
      { id: string; updates: Partial<Property> }
    >({
      query: ({ id, updates }) => ({
        url: `/properties/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Property', id }, 'Property'],
    }),

    deleteProperty: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `/properties/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Property'],
    }),

    // User management endpoints
    getUsers: builder.query<ApiResponse<User[]>, void>({
      query: () => '/users',
      providesTags: ['User'],
    }),

    getUser: builder.query<ApiResponse<User>, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),

    createUser: builder.mutation<ApiResponse<User>, Partial<User>>({
      query: (userData) => ({
        url: '/users',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    deleteUser: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
})

export const {
  // Auth
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
  
  // User
  useGetCurrentUserQuery,
  useUpdateUserMutation,
  
  // Alerts
  useGetAlertsQuery,
  useGetAlertQuery,
  useAcknowledgeAlertMutation,
  useAcknowledgeMultipleAlertsMutation,
  useCreateWorkOrderFromAlertMutation,
  
  // Work Orders
  useGetWorkOrdersQuery,
  useGetWorkOrderQuery,
  useCreateWorkOrderMutation,
  useUpdateWorkOrderMutation,
  useDeleteWorkOrderMutation,
  useAssignWorkOrderMutation,
  useUpdateWorkOrderProgressMutation,
  
  // Equipment
  useGetEquipmentQuery,
  useGetEquipmentByIdQuery,
  useCreateEquipmentMutation,
  useUpdateEquipmentMutation,
  useDeleteEquipmentMutation,
  useGetEquipmentHistoryQuery,
  useScheduleMaintenanceForEquipmentMutation,
  
  // Analytics
  useGetDashboardAnalyticsQuery,
  useGetEquipmentAnalyticsQuery,
  useGetCostAnalyticsQuery,
  
  // Real-time
  useGetRealtimeDataQuery,
  
  // File upload
  useUploadFileMutation,

  // Properties
  useGetPropertiesQuery,
  useGetPropertyQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,

  // Users
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
} = api